import produce from 'immer';
import { isUndefined } from 'lodash';

import System from '../../config/SystemConfig';
import { ComposedActions, CustomAction } from '../../helpers/actions';
import {
  CatizMonitors,
  DisplayConstants,
  ExecutorActions,
  LessonState,
  MapRenderState,
  NotsSystem,
  PolygonTypes,
  S80ExecutorActions,
  S80Monitors,
  UndoableActions,
} from '../../helpers/constants';
import {
  MultimediaAndHintReviewActionPayload,
  ShowMultimediaAndHintReviewActionPayload,
} from '../../helpers/shared-actions.types';
import { arrayToObjParams, isEmpty } from '../../helpers/utils';
import { convertToNorthAngleInRadians } from '../../utils/math.util';
import { types as executorActions } from '../actions/executor.actions';
import {
  CatizInitialState,
  default as catizReducer,
} from './catiz_executor.reducer';
import {
  default as hermesysReducer,
  hermesysInitialState,
} from './hermesys.reducer';
import {
  default as s80Reducer,
  initialState as s80InitialState,
} from './s80_executor.reducer';
import tctReducer, { tctInitialState } from './tct.reducer';

const initialState = {
  status: LessonState.INITIAL,
  context: '',
  windows: {},
  displayWindows: {},
  multimediaText: {},
  pendingAnimations: 0,
  pendingNonBlockingAnimations: 0,
  drawingsOnMap: [],
  tracks: [],
  trackConnector: [],
  resources: {},
  touchPanelVisibility: {},
  touchPanelText: {},
  resourcesLoadingStatus: {},
  tacsitBackground: {},
  tracksOnImage: [],
  s80: s80InitialState,
  catiz: CatizInitialState,
  hermesys: hermesysInitialState,
  tct: tctInitialState,
  checkpoint: {},
  labelReferences: {},
  hookedItems: {
    primary: [],
    secondary: [],
    primaryHookId: '',
    secondaryHookId: '',
  },
  wayPoints: {},
  simplePoints: [],
  polygons: {},
  alerts: [],
  reviewAlert: {},
  activeRole: 'NONE',
  preferences: {
    dialogMonitor: 'lower', // each system may have its own monitor config, this is just a string representation.
  },
  monitorMaps: {
    [S80Monitors.UPPER]: {},
    [S80Monitors.LOWER]: {},
    [CatizMonitors.MainMapArea]: {},
    [CatizMonitors.GFCSMapArea]: {},
  },
  uiState: {
    mapRender: {},
  },
  multimediaAndHintReviews: {
    list: [],
    displayIndex: -1,
  },
};

const updateDialogMonitor = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { monitor } = payload;
    draftState.preferences.dialogMonitor = monitor;
  });

const updateActiveRole = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const action = arrayToObjParams(actionPayload);
    draftState.activeRole = action.role;
  });
const resetPrimaryHook = (draftState: any) => {
  draftState.hookedItems.primaryHookId = '';
  draftState.hookedItems.primary = [];
};

const resetSecondaryHook = (draftState: any) => {
  draftState.hookedItems.secondaryHookId = '';
  draftState.hookedItems.secondary = [];
};

const resetHookIfHooked = (draftState: any, identifiers: Array<string>) => {
  console.log({ identifiers });
  if (identifiers.includes(draftState.hookedItems.primaryHookId)) {
    resetPrimaryHook(draftState);
  } else if (identifiers.includes(draftState.hookedItems.secondaryHookId)) {
    resetSecondaryHook(draftState);
  }
};

const completeMapLoading = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    draftState.uiState.mapRender[payload.monitor] = MapRenderState.COMPLETE;
  });

const updateMonitorMaps = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { monitor, position, mapObj } = payload;
    if (System.system === NotsSystem.S80) {
      if (monitor === 'both') {
        draftState.monitorMaps = {};
        draftState.monitorMaps[S80Monitors.UPPER] = {
          zoomPosition: position,
          map: mapObj,
        };
        draftState.monitorMaps[S80Monitors.LOWER] = {
          zoomPosition: position,
          map: mapObj,
        };
        draftState.uiState.mapRender[S80Monitors.UPPER] =
          MapRenderState.STARTED;
        draftState.uiState.mapRender[S80Monitors.LOWER] =
          MapRenderState.STARTED;
      } else {
        draftState.uiState.mapRender[monitor] = MapRenderState.STARTED;
        draftState.monitorMaps = {
          ...state.monitorMaps,
          [monitor]: {
            zoomPosition: position,
            map: mapObj,
          },
        };
      }
    } else if (System.system === NotsSystem.CATIZ) {
      draftState.monitorMaps = {};
      // TODO: After builder changes are done, change this
      draftState.monitorMaps[CatizMonitors.MainMapArea] = {
        zoomPosition: position,
        map: mapObj,
      };
      draftState.uiState.mapRender[CatizMonitors.MainMapArea] =
        MapRenderState.STARTED;
    }
  });

const zoomInMap = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { monitor } = payload;
    draftState.monitorMaps[monitor].zoomPosition = Math.min(
      +state.monitorMaps[monitor].zoomPosition + 1,
      Object.keys(state.monitorMaps[monitor].map.mapByScale).length
    );
  });

const zoomOutMap = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { monitor } = payload;
    draftState.monitorMaps[monitor].zoomPosition = Math.max(
      +state.monitorMaps[monitor].zoomPosition - 1,
      1
    );
  });

const placeSimplePoint = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const actionObj = arrayToObjParams(actionPayload);
    draftState.simplePoints = [
      ...state.simplePoints,
      { ...actionObj, visible: true },
    ];
  });

const removeWaypoint = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const { identifier } = arrayToObjParams(actionPayload);
    delete draftState.wayPoints[identifier];
    resetHookIfHooked(draftState, [identifier]);
  });

const placeWayPoint = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const actionObj = arrayToObjParams(actionPayload);
    draftState.wayPoints[actionObj.wpn] = {
      ...actionObj,
      visible: true,
      selected: false,
    };
  });

const removePolygonCircle = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const { identifier } = arrayToObjParams(actionPayload);
    delete draftState.polygons[identifier];
    resetHookIfHooked(draftState, [identifier]);
  });

const removePolylineOrPolygon = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const { identifier } = arrayToObjParams(actionPayload);
    const selectedPolyline = state.polygons[identifier];
    delete draftState.polygons[identifier];
    resetHookIfHooked(draftState, [
      identifier,
      ...selectedPolyline.seqOfPoints.map((point: any) => point.pointLabel),
    ]);
  });

const unhookTrack = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const { identifier } = arrayToObjParams(actionPayload);
    resetHookIfHooked(draftState, [identifier]);
  });

const updateHooksOnAnimation = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { position, trackIdentifier } = payload;
    if (state.hookedItems.primaryHookId === trackIdentifier) {
      draftState.hookedItems.primary = state.hookedItems.primary.map(
        (hookProp) => {
          if (hookProp.lat) {
            return { lat: position.latitude };
          } else if (hookProp.lon) {
            return { lon: position.longitude };
          }
          return hookProp;
        }
      );
    }

    if (state.hookedItems.secondaryHookId === trackIdentifier) {
      draftState.hookedItems.secondary = state.hookedItems.secondary.map(
        (hookProp) => {
          if (hookProp.lat) {
            return { lat: position.latitude };
          } else if (hookProp.lon) {
            return { lon: position.longitude };
          }
          return hookProp;
        }
      );
    }
  });

const hookPoint = (
  state: any,
  payload: any,
  isPrimaryHooked: boolean,
  isSecondaryHooked: boolean
) =>
  produce(state, (draftState: any) => {
    const { latitude, longitude, hookProperties } = payload;

    if (isPrimaryHooked) {
      draftState.hookedItems.primary = setHookedProperties(
        latitude,
        longitude,
        hookProperties
      );
      draftState.hookedItems.primaryHookId = payload.identifier;
    }
    if (isSecondaryHooked) {
      draftState.hookedItems.secondary = setHookedProperties(
        latitude,
        longitude,
        hookProperties
      );
      draftState.hookedItems.secondaryHookId = payload.identifier;
    }
  });

const setHookedProperties = (
  lat: string,
  long: string,
  hookProperties: Array<any> = []
) => {
  let isLatSet = false;
  let isLongSet = false;
  const hookPropertiesNew = hookProperties.map((hookProp: any) => {
    if (!isUndefined(hookProp.lat) || !isUndefined(hookProp.LAT)) {
      isLatSet = true;
      return { lat: lat };
    }
    if (!isUndefined(hookProp.lon) || !isUndefined(hookProp.LON)) {
      isLongSet = true;
      return { lon: long };
    }
    return hookProp;
  });
  // const extraProps: Array<any> = [];
  // if (!isLatSet) {
  //   extraProps.push({ latitude: lat });
  // }
  // if (!isLongSet) {
  //   extraProps.push({ longitude: long });
  // }
  // return [...hookProperties, ...extraProps];
  return [...hookPropertiesNew];
};

/* Improvement: If there are no primary and secondary hooks,
 *  then this function should not update the state.
 * Behaviour: Will replace all the properties of primary or secondary hook function
 * with the new properties. If the new properties do not have lat or lon, then the
 * lat and lon of the store will be used if present.
 */
const updateHookProperties = (state: any, payload: any, key: string) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const action = arrayToObjParams(actionPayload);
    const { hookProperties } = action;
    draftState.hookedItems[key] = hookProperties.map((property: any) => {
      if (!isUndefined(property.lat) || !isUndefined(property.LAT)) {
        return {
          lat: state.hookedItems[key].find((item: any) => item.lat)?.lat,
        };
      } else if (!isUndefined(property.lon) || !isUndefined(property.LON)) {
        return {
          lon: state.hookedItems[key].find((item: any) => item.lon)?.lon,
        };
      }
      return property;
    });
  });

const onPlacePolygon = (state: any, payload: any, type: string) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const { label, ...restProps } = actionPayload;
    draftState.polygons[actionPayload.arn || actionPayload.rn] = {
      type,
      ...restProps,
    };
  });

const onShowChannel = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const action = arrayToObjParams(actionPayload);
    const { identifier, width } = action;
    draftState.polygons[identifier] = {
      ...draftState.polygons[identifier],
      showChannelPath: true,
      channelWidth: width,
    };
  });

const updateTacitPolygonColorAndStuff = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const actionObj = arrayToObjParams(actionPayload);
    const { label, identifier, ...restProps } = actionObj;
    draftState.polygons[identifier] = {
      ...state.polygons[identifier],
      ...restProps,
    };
  });

const updateAlerts = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const action = arrayToObjParams(actionPayload);
    draftState.alerts = [
      ...state.alerts,
      {
        title: action.title,
        description: action.description,
        fontColor: action.fontColor,
        pri: action?.pri,
        sec: action?.sec,
      },
    ];
  });

const updateReviewAlerts = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    draftState.reviewAlert = state.alerts[0];
    draftState.alerts = state.alerts.slice(1);
  });

const removeAlerts = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const action = arrayToObjParams(actionPayload);
    draftState.alerts = state.alerts.filter(
      (alert: any) => alert.title !== action.title
    );
  });

const removeAllAlerts = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    draftState.alerts = [];
  });

const updateTouchPanelText = (state: any, payload: any) =>
  produce(state, (drafState: any) => {
    const { actionPayload } = payload;
    const panelText = arrayToObjParams(actionPayload);
    drafState.touchPanelText = {
      ...state.touchPanelText,
      [panelText.TextArea]: panelText.TexToShow,
    };
  });

const updateTouchPanelVisibility = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const panelUpdates = arrayToObjParams(actionPayload);
    panelUpdates['PanelToShowId'].split(',').forEach((showId: string) => {
      draftState.touchPanelVisibility[showId] = true;
    });
    panelUpdates['PanelsToHideIds'].split(',').forEach((showId: string) => {
      draftState.touchPanelVisibility[showId] = false;
    });
  });

const updateDrawingsOnMap = (state: any, payload: any, drawingType: string) =>
  produce(state, (drafState: any) => {
    const { actionPayload } = payload;
    const draw = arrayToObjParams(actionPayload);
    draw.type = drawingType;
    drafState.drawingsOnMap = [...state.drawingsOnMap, draw];
  });

const updateStatus = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { status } = payload;
    draftState.status = status;
  });

const updatePendingAnimation = (state: any, payload: any) =>
  produce(state, (drafState: any) => {
    const { count, isNonBlocking } = payload;
    if (isNonBlocking) {
      drafState.pendingNonBlockingAnimations =
        state.pendingNonBlockingAnimations + count;
    } else {
      drafState.pendingAnimations = state.pendingAnimations + count;
    }
  });

const updateWindow = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const { window, ...parameters } = arrayToObjParams(actionPayload);
    draftState.windows[window] = state.windows[window] || {};
    Object.keys(parameters).map((parameter: any) => {
      // const valueGiven = parameters[parameter];
      // if (valueGiven.startsWith('$ref.')) {
      //   const reference = valueGiven.substr(5);
      //   const val = get(state.labelReferences, reference);
      //   if (val) {
      //     draftState.windows[window][parameter] = val;
      //   } else {
      //     draftState.windows[window][parameter] = parameters[parameter];
      //   }
      // } else {
      //   draftState.windows[window][parameter] = parameters[parameter];
      // }
      draftState.windows[window][parameter] = parameters[parameter];
    });
  });

const userUpdateWindowByToggleButton = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { windowId, ...rest } = payload;
    const parameter = rest['key'];
    draftState.windows[windowId] = state.windows[windowId] || {};
    draftState.windows[windowId][parameter] = payload.value;
  });

const userUpdateWindow = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { windowId, value } = payload;
    draftState.windows[windowId] = {
      ...(state.windows[windowId] || {}),
      ...value,
    };
  });

const updateAssetDialog = (state: any, payload: any, type: 'image' | 'video') =>
  produce(state, (drafState: any) => {
    const { actionPayload } = payload;
    const params = arrayToObjParams(actionPayload);
    drafState.multimediaText = {
      asset: params.asset,
      xPosition: params.xPosition,
      yPosition: params.yPosition,
      duration: params.duration,
      type,
    };
  });

const clearMultimediaText = (state, payload) =>
  produce(state, (draftState: any) => {
    draftState.multimediaText = {};
  });

const updateMultimediaText = (
  state: any,
  payload: any,
  isHint: boolean = false
) =>
  produce(state, (draftState: any) => {
    const {
      asset,
      xPosition,
      yPosition,
      duration,
      hint,
      isError,
      noActionButtons,
    } = payload.actionPayload;
    let displayText = '';
    if (isHint) {
      displayText = hint;
    }
    draftState.multimediaText = {
      asset,
      xPosition,
      yPosition,
      duration,
      type: isHint ? 'hint' : 'multimedia',
      hint,
      displayText,
      isError,
      noActionButtons,
    };
  });
const updateContext = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { context } = payload;
    draftState.context = context;
    draftState.status = LessonState.STARTED;
  });
const updateMode = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { mode } = payload;
    draftState.context = {
      mode,
    };
  });

const showWindow = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const windowObj = arrayToObjParams(actionPayload);
    delete draftState.displayWindows[windowObj.window];
    draftState.displayWindows[windowObj.window] = {
      ...windowObj,
      displayArea:
        windowObj.displayArea === 'default'
          ? 'generalDisplayArea'
          : windowObj.displayArea,
      monitor: state.s80.touchPanelVariables.activeMonitor,
      visible: true,
    };
  });

const showCatizWindow = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { window, tabTitle } = payload.actionPayload;

    draftState.displayWindows[window] = {
      window: window,
      displayArea: tabTitle,
      monitor: 'upper', // meaningless here, adding just for compatibility
      visible: true,
    };
  });

const hideWindow = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const windowObj = arrayToObjParams(actionPayload);
    draftState.displayWindows[windowObj.window] &&
      (draftState.displayWindows[windowObj.window].visible = false);
  });

const setTrack = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const trackPayload = arrayToObjParams(actionPayload);
    draftState.tracks = [...state.tracks, trackPayload];
  });

const updateTrackSymbol = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const trackPayload = arrayToObjParams(actionPayload);
    draftState.tracks = state.tracks.map((track: any) => {
      if (track.trackNumber === trackPayload.existingTrackNumber) {
        return {
          ...track,
          symbol: trackPayload.symbol,
        };
      }
      return track;
    });
  });

const updateTascitImage = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const action = arrayToObjParams(actionPayload);
    if (action.monitor == 'both') {
      draftState.tacsitBackground[DisplayConstants.UpperMonitor] = {
        asset: action.asset,
        assetContent: action.assetContent,
      };
      draftState.tacsitBackground[DisplayConstants.LowerMonitor] = {
        asset: action.asset,
        assetContent: action.assetContent,
      };
      return;
    }
    draftState.tacsitBackground[action.monitor] = {
      asset: action.asset,
      assetContent: action.assetContent,
    };
  });

const placeSymbolOnTacsit = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const actionObj = arrayToObjParams(actionPayload);
    const { xyPoint } = actionObj;
    draftState.tracksOnImage = [
      ...state.tracksOnImage,
      {
        ...actionObj,
        x: xyPoint.x,
        y: xyPoint.y,
      },
    ];
  });

const moveSymbolOnTacsitImage = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const actionObj = arrayToObjParams(actionPayload);
    draftState.tracksOnImage = state.tracksOnImage.map((track: any) => {
      if (track.identifier === actionObj.existingIdentifier) {
        return {
          ...track,
          movement: actionObj,
        };
      }
      return track;
    });
  });

const removeTrack = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const { identifier } = arrayToObjParams(actionPayload);
    draftState.tracks = state.tracks.filter(
      (track: any) => track.trackNumber !== identifier
    );
  });

const updateTrack = (state: any, payload: any) =>
  produce(state, (drafState: any) => {
    const { actionPayload } = payload;
    const {
      track,
      radius,
      innerAreaImage,
      innerAreaHeight,
      innerAreaWidth,
      color,
      strokeWidth,
    } = arrayToObjParams(actionPayload);
    drafState.tracks = state.tracks.map((t: any) => {
      if (t.track !== track) {
        return t;
      } else {
        const innerImage = state.resources[innerAreaImage];
        return {
          ...t,
          coverage: {
            radius,
            innerAreaHeight,
            innerAreaWidth,
            innerImage,
            color,
            strokeWidth,
          },
        };
      }
    });
  });

const addResources = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { name, fileDetails } = payload;
    draftState.resources[name] = fileDetails;
  });

const moveTrackThroughTrajectory = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const trackPayload = arrayToObjParams(actionPayload);
    draftState.tracks = state.tracks.map((t: any) => {
      console.log('[executor]', t.track, trackPayload.track);
      if (t.trackNumber === trackPayload.existingTrackNumber) {
        return {
          ...t,
          moveThroughTrajectory: trackPayload.trajectory,
          showTrajectory: trackPayload.visible,
        };
      } else {
        return t;
      }
    });
  });

const moveTrack = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const trackToMove = arrayToObjParams(actionPayload);
    draftState.tracks = state.tracks.map((t: any) => {
      console.log('[executor]', t.track, trackToMove.track);
      if (t.track === trackToMove.track) {
        return {
          ...t,
          doMovement: true,
        };
      } else {
        return t;
      }
    });
  });

const updateWindowTableData = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const { window, table } = actionPayload;
    const { tableId, colId, rowNumber, value } = table;
    const existingData = state.windows[window][tableId];
    const newData = existingData.map((existingItem: any) => {
      if (existingItem.rowNumber === rowNumber) {
        return {
          ...existingItem,
          [colId]: value,
        };
      }
      return existingItem;
    });
    draftState.windows[window][tableId] = newData;
  });

const updateWindowDataItem = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const action = arrayToObjParams(actionPayload);
    console.log('updateWindowDataItem', action);
    // const keysToUpdate = Object.keys(action).filter(
    //   (key: any) => key !== 'window'
    // );
    const { window, label, ...keysToUpdate } = action;
    const updatedData = Object.keys(keysToUpdate).reduce((prev, key) => {
      const existingData = state.windows[action.window][key];
      const newData = existingData.map((existingItem: any) => {
        const item = action[key].find(
          (row: any) => row.rowNumber === existingItem.rowNumber
        );
        if (item) {
          return {
            ...existingItem,
            ...item,
          };
        }
        return existingItem;
      });
      // SystemupdatewindowDataItems can add new rows
      const newRowItems = action[key].filter(
        (row: any) =>
          !newData.find((item: any) => item.rowNumber === row.rowNumber)
      );
      return {
        ...prev,
        [key]: [...newData, ...newRowItems],
      };
    }, {});
    draftState.windows[action.window] = {
      ...state.windows[action.window],
      ...updatedData,
    };
  });

const updateLabel = (state: any, action: any) =>
  produce(state, (draftState: any) => {
    const { payload, type } = action;
    const { actionPayload } = payload;
    const actionObj = arrayToObjParams(actionPayload);
    if (actionObj) {
      actionObj.type = type;
      draftState.labelReferences = {
        ...state.labelReferences,
        [actionObj.label]: actionObj,
      };
    }
  });

const labelReducer = (state: any, action: any) => {
  const { type, payload } = action;
  if (payload) {
    const { actionPayload } = payload;
    if (actionPayload) {
      const actionObj = arrayToObjParams(actionPayload);
      if (actionObj && actionObj.label) {
        return updateLabel(state, action);
      }
      return state;
    }
  }
  return state;
};

const putCircleAroundTrack = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const actionObj = arrayToObjParams(actionPayload);
    draftState.tracks = state.tracks.map((track: any) => {
      if (track.trackNumber !== actionObj.trackIdentifier) {
        return track;
      }
      const newTrackObj = {
        ...track,
        circles: [
          ...(track.circles || []),
          {
            radius: actionObj.radius,
            lineType: actionObj.lineType,
          },
        ],
      };
      return newTrackObj;
    });
  });

const updateTrackAnimatedPosition = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    draftState.tracks = state.tracks.map((track: any) => {
      if (track.trackNumber !== payload.trackIdentifier) {
        return track;
      }
      const newTrackObj = {
        ...track,
        animatedPosition: {
          ...payload.position,
        },
      };
      return newTrackObj;
    });
  });

const connectTracks = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const actionObj = arrayToObjParams(actionPayload);
    draftState.trackConnector = [
      ...state.trackConnector,
      {
        sourceTrack: actionObj.trackOneIdentifier,
        targetTrack: actionObj.trackTwoIdentifier,
        lineType: actionObj.lineType,
        lineColor: actionObj.lineColor,
      },
    ];
  });

const disconnectTracks = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const actionObj = arrayToObjParams(actionPayload);
    draftState.trackConnector = state.trackConnector.filter(
      (connector: any) => {
        if (
          connector.sourceTrack === actionObj.trackOneIdentifier &&
          connector.targetTrack === actionObj.trackTwoIdentifier
        ) {
          return false;
        }
        if (
          connector.targetTrack === actionObj.trackOneIdentifier &&
          connector.sourceTrack === actionObj.trackTwoIdentifier
        ) {
          return false;
        }
        return true;
      }
    );
  });

const updateRectangle = (state, payload) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    draftState.tracks = state.tracks.map((track: any) => {
      return {
        ...track,
        rectangles: track.rectangles.map((rectangle) => {
          if (rectangle.trackRectId === actionPayload.trackRectIdentifier) {
            return {
              ...rectangle,
              ...actionPayload,
            };
          }
          return rectangle;
        }),
      };
    });
  });

const putRectangleAroundTrack = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const actionObj = arrayToObjParams(actionPayload);
    draftState.tracks = state.tracks.map((track: any) => {
      if (track.trackNumber !== actionObj.trackIdentifier) {
        return track;
      }
      const newTrackObj = {
        ...track,
        rectangles: [
          ...(track.rectangles || []),
          {
            ...actionObj,
            center: actionObj.center,
            width: actionObj.width,
            length: actionObj.length,
            rotation: convertToNorthAngleInRadians(actionObj.rotation),
            pointPosition: actionObj.pointPosition,
          },
        ],
      };
      return newTrackObj;
    });
  });

const updateWindowCountdownAttr = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const actionObj = arrayToObjParams(actionPayload);
    const { window, countdownType, from, to, speed, fieldId } = actionObj;
    draftState.windows[window] = {
      ...(state.windows[window] || {}),
      [fieldId]: {
        countdownType,
        from,
        to,
        speed,
        enabled: true,
      },
    };
  });

const stopWindowCountdownAttr = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const actionObj = arrayToObjParams(actionPayload);
    const { window, fieldIdReference } = actionObj;
    const existingProps = state.windows[window] || {};
    draftState.windows[window] = {
      ...existingProps,
      [fieldIdReference]: {
        ...existingProps[fieldIdReference],
        enabled: false,
      },
    };
  });

const onSetWaypointsFromResource = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const actionObj = arrayToObjParams(actionPayload);
    const { pointsReference } = actionObj;
    const wayPoints = actionObj[pointsReference];
    const {
      window,
      predefinedDataItem,
      pointsReference: x,
      ...rest
    } = actionObj;
    const newWayPoints: any = {};
    wayPoints.forEach((wayPoint: any) => {
      newWayPoints[wayPoint.WPN] = {
        ...wayPoint,
        fullFormat: true,
        visible: true,
        selected: false,
      };
    });
    draftState.wayPoints = {
      ...state.wayPoints,
      ...newWayPoints,
    };
    draftState.windows[window] = {
      ...rest,
    };
  });

const addMultimediaAndHintReview = (
  state: any,
  payload: MultimediaAndHintReviewActionPayload
) =>
  produce(state, (draftState: any) => {
    const { alertType, actionPayload } = payload;

    draftState.multimediaAndHintReviews.list = [
      {
        alertType,
        actionPayload,
      },
      ...state.multimediaAndHintReviews.list,
    ];
  });

const showMultimediaAndHintReview = (
  state: any,
  payload: ShowMultimediaAndHintReviewActionPayload
) =>
  produce(state, (draftState: any) => {
    const { index } = payload;
    draftState.multimediaAndHintReviews.displayIndex = index;
  });

const dismissMultimediaAndHintReview = (state: any) =>
  produce(state, (draftState: any) => {
    draftState.multimediaAndHintReviews.displayIndex = -1;
  });

const commonReducer = (state = initialState, action: any) => {
  const { type, payload } = action;
  switch (type) {
    case executorActions.UPDATE_DIALOG_MONITOR:
      return updateDialogMonitor(state, payload);
    case executorActions.EXECUTOR_STATUS_UPDATE:
      return updateStatus(state, payload);
    case executorActions.END_OF_LESSON:
      return updateStatus(state, { status: LessonState.STOPPED });
    case ExecutorActions.UserSetWindowAttribute:
    case ExecutorActions.SystemSetWindowAttributes:
    // case ComposedActions.PushWindowToggleButton.id:
    case S80ExecutorActions.SetWindowDataItems:
    case ComposedActions.SystemSetWindowButtonColor.id:
    case ComposedActions.SystemSetWindowDropdownChoices.id:
    case ComposedActions.SystemSetDataItemsFromResource.id:
    case ComposedActions.SystemUpdateWindowDropdownChoices.id:
      return updateWindow(state, payload);
    case ComposedActions.PushWindowTextToggleButton.uiAction:
      return userUpdateWindowByToggleButton(state, payload);
    case ExecutorActions.SystemSetWindowCountdownAttribute:
      return updateWindowCountdownAttr(state, payload);
    case ComposedActions.SystemStopWindowCountdown.id:
      return stopWindowCountdownAttr(state, payload);
    case ExecutorActions.SetWaypointsFromResource:
      return onSetWaypointsFromResource(state, payload);
    case ComposedActions.SelectWindowDataItems.uiAction:
    case ComposedActions.UnselectWindowDataItems.uiAction:
    case S80ExecutorActions.SystemDeleteWindowDataItem:
    case S80ExecutorActions.SystemUpdateWindowDataItems:
    case ComposedActions.SystemUnselectWindowDataItems.id:
    case ComposedActions.SystemSelectWindowDataItems.id:
    case ComposedActions.SelectWindowDataItemCell.uiAction:
    case ComposedActions.UnselectWindowDataItemCell.uiAction:
    case ComposedActions.SystemSelectWindowDataItemCell.id:
    case ComposedActions.SystemUnselectWindowDataItemCell.id:
      return updateWindowDataItem(state, payload);
    case ComposedActions.UserClickWindowDataItemCellCheckbox.uiAction:
      return updateWindowTableData(state, payload);
    case ComposedActions.PushWindowToggleButton.uiAction:
      return userUpdateWindowByToggleButton(state, payload);
    case executorActions.UI_USER_SET_WINDOW_ATTRIBUTE:
      return userUpdateWindow(state, payload);
    case ExecutorActions.SystemPlaceWayPoint:
      return placeWayPoint(state, payload);
    case executorActions.START_EXECUTOR:
      return updateContext(state, payload);
    case ExecutorActions.ShowAlert:
      return updateAlerts(state, payload);
    case ComposedActions.SystemReviewAlert.id:
      return updateReviewAlerts(state, payload);
    case ExecutorActions.SystemRemoveAlert:
      return removeAlerts(state, payload);
    case ExecutorActions.SystemClearAllAlerts:
      return removeAllAlerts(state, payload);
    case executorActions.UPDATE_EXECUTION_MODE:
      return updateMode(state, payload);
    case executorActions.MULTIMEDIA_OK_CLICK:
      return clearMultimediaText(state, payload);
    case ExecutorActions.ShowMultimediaText:
      return updateMultimediaText(state, payload);
    case ExecutorActions.ShowWindow:
      return showWindow(state, payload);
    case ExecutorActions.HideWindow:
      return hideWindow(state, payload);
    case ExecutorActions.ShowHint:
      return updateMultimediaText(state, payload, true);
    case executorActions.UPDATE_PENDING_ANIMATIONS:
      return updatePendingAnimation(state, payload);
    case ExecutorActions.ShowNoGoZone:
      return updateDrawingsOnMap(state, payload, 'NoGoZone');
    case ExecutorActions.ShowCoastalLine:
      return updateDrawingsOnMap(state, payload, 'CoastalLine');
    case ExecutorActions.SetTrackAtPosition:
    case ExecutorActions.SystemPlaceTrackSymbolAtPosition:
      return setTrack(state, payload);
    case ExecutorActions.ShowCoverageArea:
      return updateTrack(state, payload);
    case ExecutorActions.TouchPanelShowHidePanels:
      return updateTouchPanelVisibility(state, payload);
    case ExecutorActions.TouchPanelShowTextInTPTA:
      return updateTouchPanelText(state, payload);
    case ExecutorActions.SystemRemoveTrack:
      return unhookTrack(removeTrack(state, payload), payload);
    case ExecutorActions.SystemDrawTrackCenteredCircle:
      return putCircleAroundTrack(state, payload);
    case ExecutorActions.SystemDrawTrackRectangle:
      return putRectangleAroundTrack(state, payload);
    case ComposedActions.SystemUpdateTrackRectangle.id:
      return updateRectangle(state, payload);
    case ExecutorActions.SystemConnectTrackGroup:
      return connectTracks(state, payload);
    case ExecutorActions.SystemDisconnectTrackGroup:
      return disconnectTracks(state, payload);
    case ExecutorActions.SystemUpdatePrimaryHookProperties:
      return updateHookProperties(state, payload, 'primary');
    case ExecutorActions.SystemUpdateSecondaryHookProperties:
      return updateHookProperties(state, payload, 'secondary');
    case executorActions.UPDATE_ANIMATED_TRACK_POSITION:
      return updateTrackAnimatedPosition(state, payload);
    case ExecutorActions.SetActiveRole:
      return updateActiveRole(state, payload);
    case ComposedActions.SystemUpdateAreaCircleProperties.id:
    case ComposedActions.SystemUpdateAreaPolygonProperties.id:
    case ComposedActions.SystemUpdatePolylineProperties.id:
      return updateTacitPolygonColorAndStuff(state, payload);

    case executorActions.INTERNAL_SET_MAP:
      return updateMonitorMaps(state, payload);
    case executorActions.RANGE_IN:
      return zoomInMap(state, payload);
    case executorActions.RANGE_OUT:
      return zoomOutMap(state, payload);
    case executorActions.INTERNAL_MAP_LOADED:
      return completeMapLoading(state, payload);

    case ExecutorActions.SetImageInTacsit:
      return updateTascitImage(state, payload);
    case ComposedActions.SystemPlaceTrackSymbolOnTacsitImage.id:
      return placeSymbolOnTacsit(state, payload);
    case ComposedActions.SystemMoveSymbolOnTacsitImage.id:
      return moveSymbolOnTacsitImage(state, payload);
    case ExecutorActions.SystemRemoveCircle:
    case ExecutorActions.SystemRemoveCircularSector:
      return removePolygonCircle(state, payload);
    case ExecutorActions.SystemRemovePolyline:
      return removePolylineOrPolygon(state, payload);
    case ExecutorActions.SystemRemovePolygon:
      return removePolylineOrPolygon(state, payload);
    case ComposedActions.SystemPlaceAreaChannel.id:
      return onShowChannel(state, payload);

    case ExecutorActions.SystemPlaceAreaCircle:
      return onPlacePolygon(state, payload, PolygonTypes.CIRCLE);
    case ExecutorActions.SystemPlacePolyline:
      return onPlacePolygon(state, payload, PolygonTypes.POLYLINE);
    case ExecutorActions.SystemPlaceAreaPolygon:
      return onPlacePolygon(state, payload, PolygonTypes.POLYGON);
    case ExecutorActions.SystemPlaceAreaCircularSector:
      return onPlacePolygon(state, payload, PolygonTypes.CIRCULAR_SECTOR);

    case executorActions.UPDATE_ANIMATED_TRACK_POSITION:
      return updateHooksOnAnimation(state, payload);
    case executorActions.INTERNAL_SET_PRIMARY_HOOK:
    case executorActions.UI_SET_PRIMARY_HOOK:
      return hookPoint(state, payload, true, false);
    case executorActions.INTERNAL_SET_SECONDARY_HOOK:
    case executorActions.UI_SET_SECONDARY_HOOK:
      return hookPoint(state, payload, false, true);
    case ExecutorActions.SystemPlacePoint:
      return placeSimplePoint(state, payload);

    case ExecutorActions.SystemRemoveWaypoint:
      return removeWaypoint(state, payload);

    // TODO: fix this to make DisplayTrackThroughTrajectory work
    // This is old function and is no longer used
    case ExecutorActions.DisplayTrackThroughTrajectory:
      return moveTrack(state, payload);
    case ExecutorActions.SystemDisplayTrackThroughTrajectory:
      return moveTrackThroughTrajectory(state, payload);
    case ExecutorActions.ShowImage:
      return updateAssetDialog(state, payload, 'image');
    case ExecutorActions.ShowVideo:
      return updateAssetDialog(state, payload, 'video');
    case ExecutorActions.SystemChangeTrackSymbol:
      return updateTrackSymbol(state, payload);
    case executorActions.RESET_EXECUTOR_STATE:
      return initialState;

    case ComposedActions.SystemShowCatizTab.id:
      return showCatizWindow(state, payload);

    case executorActions.ADD_MULTIMEDIA_HINT_REVIEW:
      return addMultimediaAndHintReview(state, payload);
    case executorActions.SHOW_MULTIMEDIA_HINT_REVIEW:
      return showMultimediaAndHintReview(state, payload);
    case executorActions.DISMISS_MULTIMEDIA_HINT_REVIEW:
      return dismissMultimediaAndHintReview(state);

    default:
      return state;
  }
};

const updateCheckpoint = (state: any) =>
  produce(state, (draftState: any) => {
    draftState.checkpoint = state;
  });

const reducer = (state = initialState, action: any) => {
  let checkPointState: any = state;
  if (checkPointState.context && checkPointState.context.mode !== 'demo') {
    if (action.type === executorActions.REVERT_TO_CHECKPOINT) {
      if (isEmpty(state.checkpoint)) {
        // throw new Error(
        //   'Cannot revert to previous lesson state, no checkpoint found.'
        // );
        return state;
      }
      return {
        ...state.checkpoint,
        multimediaText: {},
      };
    }
    if (action.type === executorActions.CLEAR_CHECKPOINT) {
      checkPointState = {
        ...checkPointState,
        checkpoint: {},
      };
      return checkPointState;
    }
    const composedUnDoableActions = Object.values(ComposedActions)
      .filter((action: CustomAction<any>) => action.unDoable)
      .map((action: CustomAction<any>) => action.uiAction);

    const unDoableActions = [...UndoableActions, ...composedUnDoableActions];

    if (unDoableActions.some((a: any) => a === action.type)) {
      checkPointState = updateCheckpoint(checkPointState);
    }
  }
  const result = commonReducer(labelReducer(checkPointState, action), action);
  const s80 = s80Reducer(checkPointState.s80, action);
  const catiz = catizReducer(checkPointState.catiz, action);
  const hermesys = hermesysReducer(checkPointState.hermesys, action);
  const tct = tctReducer(checkPointState.tct, action);
  return {
    ...result,
    s80,
    catiz,
    hermesys,
    tct,
  };
};

export default reducer;
